import { useListDashboards, type DashboardsDashboardSummary } from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import {
  AlertTriangle,
  ChevronRight,
  GripVertical,
  LayoutDashboard,
  Plus,
  Trash2,
} from 'lucide-react';
import { memo, useMemo, useState, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import type { ConfigFormProps, RendererProps, WidgetDefinition } from '../types';

const MAX_DASHBOARD_LINKS = 8;

/**
 * Dashboard Links widget config schema
 */
export const dashboardLinksConfigSchema = z.object({
  dashboardIds: z
    .array(z.string())
    .min(1, 'At least one dashboard is required')
    .max(MAX_DASHBOARD_LINKS, `Up to ${MAX_DASHBOARD_LINKS} dashboards can be linked`),
  dashboardLabels: z.record(z.string()).optional(),
});

export type DashboardLinksConfig = z.infer<typeof dashboardLinksConfigSchema>;

interface ConfigDashboardItem {
  id: string;
  name: string;
  description?: string;
  isShared?: boolean;
  exists: boolean;
}

const reorderItems = <T,>(items: T[], fromIndex: number, toIndex: number) => {
  if (fromIndex === toIndex) return items;
  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return items;
  const clampedIndex = Math.min(Math.max(toIndex, 0), next.length);
  next.splice(clampedIndex, 0, moved);
  return next;
};

/**
 * Config form for Dashboard Links widget
 */
function DashboardLinksConfigForm({
  config,
  onChange,
  errors,
}: ConfigFormProps<DashboardLinksConfig>) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { data: dashboardsData, isLoading } = useListDashboards();
  const dashboards = useMemo(() => dashboardsData?.dashboards ?? [], [dashboardsData?.dashboards]);
  const dashboardIds = useMemo(() => config.dashboardIds ?? [], [config.dashboardIds]);
  const dashboardLabels = useMemo(() => config.dashboardLabels ?? {}, [config.dashboardLabels]);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [previewIds, setPreviewIds] = useState<string[] | null>(null);
  const [draggedHeight, setDraggedHeight] = useState<number | null>(null);

  const displayIds = previewIds ?? dashboardIds;

  const configuredDashboards: ConfigDashboardItem[] = useMemo(() => {
    return displayIds.map((id) => {
      const dashboard = dashboards.find((item) => item.id === id);
      return {
        id,
        name: dashboard?.name || id,
        description: dashboard?.description ?? undefined,
        isShared: dashboard ? dashboard.isShared || dashboard.isEditable === false : false,
        exists: !!dashboard,
      };
    });
  }, [dashboards, displayIds]);

  const unselectedDashboards = useMemo(
    () =>
      dashboards.filter((dashboard) =>
        dashboard.id ? !dashboardIds.includes(dashboard.id) : false
      ),
    [dashboards, dashboardIds]
  );

  const orphanCount = configuredDashboards.filter((item) => !item.exists).length;
  const hasReachedLimit = dashboardIds.length >= MAX_DASHBOARD_LINKS;

  const handleDragStart = (event: DragEvent<HTMLDivElement>, id: string) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    const dragClone = event.currentTarget.cloneNode(true) as HTMLElement;
    dragClone.style.position = 'absolute';
    dragClone.style.top = '-9999px';
    dragClone.style.left = '-9999px';
    dragClone.style.width = `${rect.width}px`;
    dragClone.style.height = `${rect.height}px`;
    dragClone.style.pointerEvents = 'none';
    document.body.appendChild(dragClone);
    event.dataTransfer.setDragImage(dragClone, offsetX, offsetY);
    window.setTimeout(() => {
      dragClone.remove();
    }, 0);
    setDraggedId(id);
    setPreviewIds(dashboardIds);
    setDraggedHeight(event.currentTarget.getBoundingClientRect().height);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', id);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setPreviewIds(null);
    setDraggedHeight(null);
  };

  const handleItemDragOver = (event: DragEvent<HTMLDivElement>, targetId: string) => {
    event.preventDefault();
    if (!draggedId || draggedId === targetId) return;
    const currentOrder = previewIds ?? dashboardIds;
    const fromIndex = currentOrder.indexOf(draggedId);
    const targetIndex = currentOrder.indexOf(targetId);
    if (fromIndex === -1 || targetIndex === -1) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const shouldInsertAfter = event.clientY > rect.top + rect.height / 2;
    let nextIndex = targetIndex + (shouldInsertAfter ? 1 : 0);
    if (fromIndex < targetIndex && shouldInsertAfter) {
      nextIndex -= 1;
    }
    const nextOrder = reorderItems(currentOrder, fromIndex, nextIndex);
    if (nextOrder.join('|') !== currentOrder.join('|')) {
      setPreviewIds(nextOrder);
    }
  };

  const handleListDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (!draggedId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const edgeThreshold = 8;
    const currentOrder = previewIds ?? dashboardIds;
    const fromIndex = currentOrder.indexOf(draggedId);
    if (fromIndex === -1) return;
    if (event.clientY < rect.top + edgeThreshold && fromIndex !== 0) {
      setPreviewIds(reorderItems(currentOrder, fromIndex, 0));
      return;
    }
    if (event.clientY > rect.bottom - edgeThreshold && fromIndex !== currentOrder.length - 1) {
      setPreviewIds(reorderItems(currentOrder, fromIndex, currentOrder.length));
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (previewIds && previewIds.join('|') !== dashboardIds.join('|')) {
      onChange({ ...config, dashboardIds: previewIds });
    }
    handleDragEnd();
  };

  const handleLabelChange = (id: string, value: string) => {
    const next = { ...dashboardLabels };
    if (value === '') {
      delete next[id];
    } else {
      next[id] = value;
    }
    onChange({ ...config, dashboardLabels: next });
  };

  const handleAddDashboard = (dashboard: DashboardsDashboardSummary) => {
    if (!dashboard.id) return;
    if (hasReachedLimit) return;
    onChange({ ...config, dashboardIds: [...dashboardIds, dashboard.id] });
    setIsDropdownOpen(false);
  };

  const handleRemoveDashboard = (id: string) => {
    const nextIds = dashboardIds.filter((itemId) => itemId !== id);
    const nextLabels = { ...dashboardLabels };
    delete nextLabels[id];
    onChange({ ...config, dashboardIds: nextIds, dashboardLabels: nextLabels });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
          Dashboards
        </label>
        <p className="mb-3 text-xs text-gray-500 dark:text-zinc-500">
          Add 1-{MAX_DASHBOARD_LINKS} dashboards and drag to reorder.
        </p>

        {orphanCount > 0 && (
          <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-500/10">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-500" />
            <span className="text-xs text-amber-700 dark:text-amber-400">
              {orphanCount} dashboard{orphanCount > 1 ? 's' : ''} no longer exist
              {orphanCount > 1 ? '' : 's'}. Remove {orphanCount > 1 ? 'them' : 'it'} to clear this
              warning.
            </span>
          </div>
        )}

        {configuredDashboards.length > 0 ? (
          <div className="flex flex-col gap-1" onDrop={handleDrop} onDragOver={handleListDragOver}>
            {configuredDashboards.map((dashboard) => {
              const isDragged = draggedId === dashboard.id;
              const placeholderStyle =
                isDragged && draggedHeight ? { height: draggedHeight } : undefined;

              return (
                <div
                  key={dashboard.id}
                  draggable
                  onDragStart={(event) => handleDragStart(event, dashboard.id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(event) => handleItemDragOver(event, dashboard.id)}
                  className={`group flex cursor-grab items-center gap-2 rounded-lg border px-2 py-2 transition-transform active:cursor-grabbing ${
                    isDragged
                      ? 'border-dashed border-blue-300/70 bg-blue-50/30 dark:border-blue-500/40 dark:bg-blue-500/10'
                      : dashboard.exists
                        ? 'border-gray-200 bg-white dark:border-white/[0.08] dark:bg-zinc-900'
                        : 'border-amber-300 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10'
                  }`}
                  style={placeholderStyle}
                >
                  {isDragged ? null : (
                    <>
                      <div className="flex-shrink-0 text-gray-300 dark:text-zinc-600">
                        <GripVertical className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {!dashboard.exists && (
                            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
                          )}
                          <span
                            className={`truncate text-sm font-medium ${
                              dashboard.exists
                                ? 'text-gray-900 dark:text-white'
                                : 'text-amber-700 dark:text-amber-400'
                            }`}
                          >
                            {dashboard.name}
                          </span>
                          {dashboard.exists && dashboard.isShared && (
                            <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 dark:bg-zinc-800 dark:text-zinc-400">
                              Shared
                            </span>
                          )}
                        </div>
                        {dashboard.description ? (
                          <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-zinc-500">
                            {dashboard.description}
                          </p>
                        ) : null}
                        {!dashboard.exists && (
                          <p className="mt-0.5 text-xs text-amber-600 dark:text-amber-500">
                            Dashboard no longer exists
                          </p>
                        )}
                        <div className="mt-2">
                          <input
                            type="text"
                            value={dashboardLabels[dashboard.id] ?? ''}
                            onChange={(event) =>
                              handleLabelChange(dashboard.id, event.target.value)
                            }
                            placeholder="Use dashboard name"
                            draggable={false}
                            onDragStart={(event) => event.stopPropagation()}
                            className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 placeholder:text-gray-400 dark:border-white/[0.12] dark:bg-zinc-900 dark:text-zinc-200 dark:placeholder:text-zinc-500"
                          />
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemoveDashboard(dashboard.id)}
                        className="flex-shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:text-zinc-500 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                        title="Remove dashboard"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center dark:border-white/[0.08] dark:bg-zinc-900/50">
            <LayoutDashboard className="mx-auto mb-2 h-8 w-8 text-gray-300 dark:text-zinc-600" />
            <p className="text-sm text-gray-500 dark:text-zinc-500">No dashboards added yet</p>
            <p className="mt-1 text-xs text-gray-400 dark:text-zinc-600">
              Add dashboards using the button below
            </p>
          </div>
        )}

        <div className="relative mt-3">
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            disabled={(unselectedDashboards.length === 0 && !isLoading) || hasReachedLimit}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-blue-400 hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-300 disabled:hover:bg-white disabled:hover:text-gray-600 dark:border-white/[0.12] dark:bg-zinc-900 dark:text-zinc-400 dark:hover:border-blue-500/50 dark:hover:bg-blue-500/10 dark:hover:text-blue-400 dark:disabled:hover:border-white/[0.12] dark:disabled:hover:bg-zinc-900 dark:disabled:hover:text-zinc-400"
          >
            <Plus className="h-4 w-4" />
            {isLoading
              ? 'Loading dashboards...'
              : hasReachedLimit
                ? `Limit reached (${MAX_DASHBOARD_LINKS})`
                : unselectedDashboards.length === 0
                  ? 'All dashboards added'
                  : 'Add dashboard'}
          </button>

          {isDropdownOpen && unselectedDashboards.length > 0 && !hasReachedLimit && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setIsDropdownOpen(false)} />
              <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-white/[0.08] dark:bg-zinc-900">
                {unselectedDashboards.map((dashboard) => {
                  const isShared = dashboard.isShared || dashboard.isEditable === false;
                  return (
                    <button
                      key={dashboard.id}
                      type="button"
                      onClick={() => handleAddDashboard(dashboard)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-gray-900 dark:text-white">
                          {dashboard.name ?? dashboard.id}
                        </div>
                        {dashboard.description ? (
                          <div className="truncate text-xs text-gray-500 dark:text-zinc-500">
                            {dashboard.description}
                          </div>
                        ) : null}
                      </div>
                      {isShared ? (
                        <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 dark:bg-zinc-800 dark:text-zinc-400">
                          Shared
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {errors?.['dashboardIds'] && (
          <p className="mt-2 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
            <AlertTriangle className="h-3.5 w-3.5" />
            {errors['dashboardIds']}
          </p>
        )}
      </div>
    </div>
  );
}

const DashboardLinksRenderer = memo(function DashboardLinksRenderer({
  config,
  widget,
}: RendererProps<DashboardLinksConfig>) {
  const { data, isLoading, isError } = useListDashboards({
    query: {
      refetchInterval: 60000,
    },
  });
  const dashboardIds = useMemo(() => config.dashboardIds ?? [], [config.dashboardIds]);
  const dashboardLabels = useMemo(() => config.dashboardLabels ?? {}, [config.dashboardLabels]);

  const dashboardsMap = useMemo(() => {
    const map = new Map<string, DashboardsDashboardSummary>();
    for (const dashboard of data?.dashboards ?? []) {
      if (dashboard.id) {
        map.set(dashboard.id, dashboard);
      }
    }
    return map;
  }, [data?.dashboards]);

  const dashboardItems = useMemo(() => {
    return dashboardIds.map((id) => {
      const dashboard = dashboardsMap.get(id);
      const labelOverride = dashboardLabels[id]?.trim();
      return {
        id,
        name: labelOverride || dashboard?.name || id,
        description: dashboard?.description,
        isShared: dashboard ? dashboard.isShared || dashboard.isEditable === false : false,
        exists: !!dashboard,
      };
    });
  }, [dashboardIds, dashboardLabels, dashboardsMap]);

  if (dashboardIds.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-zinc-500">No dashboards configured</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <AlertTriangle className="mb-2 h-5 w-5 text-amber-500" />
        <p className="text-xs text-gray-500 dark:text-zinc-500">Failed to load dashboards</p>
      </div>
    );
  }

  const widgetWidth = widget.w ?? dashboardLinksWidget.defaultSize.w;
  const widgetHeight = widget.h ?? dashboardLinksWidget.defaultSize.h;
  const aspectRatio = widgetHeight > 0 ? widgetWidth / widgetHeight : 1;
  const maxColumns = Math.min(dashboardItems.length, Math.max(1, widgetWidth));

  let columnCount = 1;
  let rowCount = dashboardItems.length;
  let bestEmpty = Number.POSITIVE_INFINITY;
  let bestBalance = Number.POSITIVE_INFINITY;
  let bestAspectDiff = Number.POSITIVE_INFINITY;

  for (let cols = 1; cols <= maxColumns; cols += 1) {
    const rows = Math.ceil(dashboardItems.length / cols);
    const emptySlots = rows * cols - dashboardItems.length;
    const balance = Math.abs(cols - rows);
    const aspectDiff = Math.abs(cols / rows - aspectRatio);

    if (
      emptySlots < bestEmpty ||
      (emptySlots === bestEmpty && balance < bestBalance) ||
      (emptySlots === bestEmpty && balance === bestBalance && aspectDiff < bestAspectDiff)
    ) {
      columnCount = cols;
      rowCount = rows;
      bestEmpty = emptySlots;
      bestBalance = balance;
      bestAspectDiff = aspectDiff;
    }
  }

  const density = widgetHeight > 0 ? rowCount / widgetHeight : rowCount;
  const isCompact = density > 1;
  const isTight = density >= 1.5;
  const rowPaddingClass = isTight ? 'px-2 py-1' : isCompact ? 'px-2.5 py-1.5' : 'px-3 py-2';
  const iconSizeClass = isTight ? 'h-3 w-3' : isCompact ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const iconGapClass = isTight ? 'gap-1.5' : 'gap-2';
  const chevronClass = isTight ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const titleClass = isTight ? 'text-xs' : isCompact ? 'text-[13px]' : 'text-sm';

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div
          className="grid h-full gap-2"
          style={{
            gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${rowCount}, minmax(0, 1fr))`,
          }}
        >
          {dashboardItems.map((dashboard) => {
            const cardClassName = `flex h-full items-center justify-between rounded-lg border ${rowPaddingClass} ${
              dashboard.exists
                ? 'border-gray-200 bg-white transition-colors hover:border-blue-300 hover:bg-blue-50/40 dark:border-white/[0.08] dark:bg-zinc-900 dark:hover:border-blue-500/50 dark:hover:bg-blue-500/10'
                : 'border-amber-300 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10'
            }`;

            const content = (
              <>
                <div className="min-w-0 flex-1">
                  <div className={`flex items-center ${iconGapClass}`}>
                    <span className="flex-shrink-0">
                      {dashboard.exists ? (
                        <LayoutDashboard
                          className={`${iconSizeClass} text-gray-500 dark:text-zinc-400`}
                        />
                      ) : (
                        <AlertTriangle className={`${iconSizeClass} text-amber-500`} />
                      )}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`truncate font-medium ${titleClass} ${
                            dashboard.exists
                              ? 'text-gray-900 dark:text-white'
                              : 'text-amber-700 dark:text-amber-400'
                          }`}
                        >
                          {dashboard.name}
                        </span>
                        {dashboard.exists && dashboard.isShared ? (
                          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 dark:bg-zinc-800 dark:text-zinc-400">
                            Shared
                          </span>
                        ) : null}
                      </div>
                      {dashboard.description ? (
                        <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-zinc-500">
                          {dashboard.description}
                        </p>
                      ) : null}
                      {!dashboard.exists ? (
                        <p className="mt-0.5 text-xs text-amber-600 dark:text-amber-500">
                          Dashboard not found
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
                {dashboard.exists ? (
                  <ChevronRight
                    className={`ml-2 ${chevronClass} text-gray-400 dark:text-zinc-500`}
                  />
                ) : null}
              </>
            );

            if (!dashboard.exists) {
              return (
                <div key={dashboard.id} className={cardClassName}>
                  {content}
                </div>
              );
            }

            return (
              <Link
                key={dashboard.id}
                to={ROUTES.DASHBOARD_DETAIL.replace(':id', dashboard.id)}
                className={cardClassName}
              >
                {content}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
});

export const dashboardLinksWidget: WidgetDefinition<DashboardLinksConfig> = {
  type: 'dashboard-links',
  displayName: 'Dashboard Links',
  description: 'Quick links to other dashboards',
  icon: LayoutDashboard,
  defaultSize: { w: 3, h: 4 },
  minSize: { w: 2, h: 2 },
  maxSize: { w: 6, h: 6 },
  configSchema: dashboardLinksConfigSchema,
  defaultConfig: {
    dashboardIds: [],
    dashboardLabels: {},
  },
  ConfigForm: DashboardLinksConfigForm,
  Renderer: DashboardLinksRenderer,
};
